import { Link } from "react-router-dom";
import { Heart, Coins, ShieldCheck, Globe, HandHeart } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";

const PENNY_DISH =
  "https://static.prod-images.emergentagent.com/jobs/40acd7df-38cb-40a5-af80-2b7bac12c4d9/images/c2fadbe20371c2f7037ea12bd324818b07c101eae7999dfbe199a3d6c68c1f12.jpeg";

export default function About() {
  return (
    <div data-testid="about-page" className="w-full flex flex-col items-center">
      {/* Hero */}
      <div className="max-w-3xl mx-auto px-4 pt-10 sm:pt-14 w-full flex flex-col items-center text-center">
        <PageHeader
          icon={Heart}
          eyebrow="Our Story & Mission"
          title="Have a penny, leave a penny."
          subtitle="Need a penny, take a penny. A dignity-first community built on an old, simple kind of hope."
        />
        <div className="w-full max-w-2xl mx-auto rounded-3xl overflow-hidden border border-border/80 shadow-sm my-2">
          <img
            src={PENNY_DISH}
            alt="A worn dish of pennies on an old corner-store counter with a handwritten sign"
            className="w-full h-auto object-cover"
            data-testid="about-hero-image"
          />
        </div>
        <p className="font-serif italic text-foreground/60 text-sm max-w-md mx-auto">
          The little dish that started it all.
        </p>
      </div>

      {/* Story */}
      <div className="max-w-2xl mx-auto px-4 py-8 sm:py-10 w-full flex flex-col items-center text-center space-y-6">
        <StoryBlock title="The little dish by the register">
          <p>
            Years ago, before everything moved online, community lived at the corner store and the
            gas station. Everybody knew each other. And by nearly every till there was a little dish
            of pennies, nickels and dimes, with a small handwritten sign:
          </p>
          <p className="font-serif italic text-bronze text-lg sm:text-xl py-1">
            "Have a penny, leave a penny. Need a penny, take a penny."
          </p>
          <p>
            There was no limit on what you could leave or what you could take. If you had a dollar
            and your total came to $1.25, you grabbed a quarter from the dish and you were covered.
            No forms. No shame. Just neighbours quietly looking out for one another.
          </p>
        </StoryBlock>

        <StoryBlock title="Why I noticed it">
          <p>
            I grew up very poor, and I always noticed that dish. To me it was more than loose
            change, it was a glimmer of hope that people really did care about each other. It was
            proof that the small idea of sharing a little of what you have, and passing it along to
            the next person who might need it, was still alive.
          </p>
          <p>
            It was a kind of saving grace. If I were ever hungry enough, I knew I could take a few
            dollars from that dish, buy some bread, milk and eggs for the next few days, and it
            wouldn't be stealing. It was allowed. It was human. That feeling stayed with me.
          </p>
        </StoryBlock>

        <StoryBlock title="What broke my heart">
          <p>
            Lately, around my town of St. John's, Newfoundland, I've seen more and more people ask
            for help, on street corners and on social media. And I've watched people be cruel.
            Strangers yelling "get a job" from their cars. Awful, heart-wrenching comments piling up
            under someone who simply asked for help.
          </p>
          <p>
            I've watched fundraisers too, where one person raises thousands in minutes while another,
            just as deserving, can't get a single dollar, not one friend, not one family member, not
            one stranger. That heartbreak, the feeling that <em>nobody cares</em>, is exactly what
            Haplap was built to end.
          </p>
        </StoryBlock>

        <StoryBlock title="So we built a place with no room for cruelty">
          <p>
            Haplap takes that nostalgic penny dish, that beacon of light, and turns it into a safe,
            moderated place where people who need help can ask without being ridiculed or forgotten.
            We have zero tolerance for bullies. The cruel comments that live in the unmoderated
            corners of the internet don't get to live here.
          </p>
          <p>
            And for the people who <em>want</em> to help, the ones who go looking every Christmas for
            a family to sponsor, we make it easy to find honest, verified neighbours from every walk
            of life and actually support them.
          </p>
        </StoryBlock>

        <StoryBlock title="The Community Tip Jar: nobody gets left out">
          <p>
            The tip jar comes straight from watching one fundraiser get thousands while the next got
            nothing. So we split it. If you drop a dollar in the jar and ten people are asking for
            help, each of them receives a share, even people you've never met.
          </p>
          <p>
            Maybe you came to support one person you know. You still end up helping everyone at the
            same time. Whether the jar holds five cents or five thousand dollars, it's shared evenly,
            so every single person feels that little bit of help tonight, and knows that someone
            cares.
          </p>
        </StoryBlock>
      </div>

      {/* Mission statement box */}
      <div className="max-w-3xl mx-auto px-4 pb-4 w-full">
        <div
          data-testid="mission-statement"
          className="w-full max-w-2xl mx-auto rounded-3xl bg-gradient-to-b from-cream to-white border border-copper/25 p-7 sm:p-10 shadow-md text-center space-y-4">
          <span className="inline-flex items-center justify-center gap-1.5 font-mono text-xs font-semibold tracking-widest uppercase bg-copper/12 text-copper px-3.5 py-1 rounded-full border border-copper/20">
            <HandHeart className="w-3.5 h-3.5" /> Our Mission
          </span>
          <p className="font-serif text-lg sm:text-2xl text-bronze leading-relaxed">
            Haplap exists to give every person a safe, judgment-free place to ask for help, and to
            give kind people an easy, trustworthy way to give it.
          </p>
          <p className="text-sm sm:text-base text-foreground/80 leading-relaxed max-w-xl mx-auto">
            We protect the people who ask with real moderation and verification, we make sure nobody
            is ever left out through the Community Tip Jar, and we hold onto that old corner-store
            promise: if you have a little, leave a little; if you need a little, take a little.
          </p>
        </div>
      </div>

      {/* Where the fee goes */}
      <div className="max-w-2xl mx-auto px-4 py-6 w-full flex flex-col items-center text-center space-y-6">
        <StoryBlock title="Where 100% of donations go, and where our small fee goes">
          <p>
            <strong>100% of every donation goes to the individual asking for help.</strong> When you
            give directly to someone, they net the full amount, the small platform fee and payment
            processing are covered on top by the donor, never taken out of the recipient.
          </p>
          <p>
            That small fee doesn't fund big salaries. So many large charities spend millions of
            donated dollars on CEO, vice-president and executive pay, sometimes only around a quarter
            of every dollar reaches the actual cause. We refuse to be that.
          </p>
          <p>
            Our fee goes toward one thing: building, growing and lifting this platform to a worldwide
            scale, so we can help change lives everywhere, not just in one town, one province, or one
            country. We want everyone on earth to have somewhere safe to turn when they need help.
          </p>
        </StoryBlock>

        {/* Values */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-xl mx-auto">
          <ValueCard icon={ShieldCheck} title="Dignity, always" text="Ask for help without judgment. Zero tolerance for cruelty or harassment." />
          <ValueCard icon={Coins} title="Nobody left out" text="The Community Tip Jar splits every gift so everyone feels supported." />
          <ValueCard icon={Heart} title="100% to people" text="Donations go to the individual. Recipients keep the full amount." />
          <ValueCard icon={Globe} title="Built to reach the world" text="Our small fee funds growth so help can reach everyone, everywhere." />
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 max-w-md mx-auto w-full pt-2">
          <Button asChild data-testid="about-cta-discover" className="flex-1 w-full min-h-[48px] rounded-full bg-copper hover:bg-copper/90 text-white">
            <Link to="/discover">Find someone to support</Link>
          </Button>
          <Button asChild variant="outline" data-testid="about-cta-faq" className="flex-1 w-full min-h-[48px] rounded-full border-border">
            <Link to="/faq">Read our FAQ</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

function StoryBlock({ title, children }) {
  return (
    <div className="w-full max-w-2xl mx-auto space-y-2.5">
      <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-bronze leading-tight">{title}</h2>
      <div className="w-16 h-1 bg-gradient-to-r from-copper via-penny to-sage rounded-full mx-auto opacity-90" />
      <div className="space-y-3 text-sm sm:text-base text-foreground/85 leading-relaxed pt-1">{children}</div>
    </div>
  );
}

function ValueCard({ icon: Icon, title, text }) {
  return (
    <div className="rounded-3xl bg-white border border-border/80 p-5 shadow-sm flex flex-col items-center text-center gap-2">
      <div className="w-11 h-11 rounded-full bg-copper/12 text-copper flex items-center justify-center">
        <Icon className="w-5 h-5" />
      </div>
      <h3 className="font-serif text-lg font-medium text-bronze">{title}</h3>
      <p className="text-xs sm:text-sm text-foreground/70 leading-relaxed">{text}</p>
    </div>
  );
}
